import { CircularProgress, ClickAwayListener, MenuItem, Popper, TableBody, TableRow, Tooltip } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import QueryString from 'qs';
import { errorMessages, exportMembersToStyledExcel } from '../../../utils/methods';
import useUserAuth from '../../../utils/useUserAuth';
import CustomCheckbox from '../../CustomCheckbox';
import { getUsersList, deleteMembersBulk } from '../../../api/member-management';
import { getExpirationAlerts, updateExpirationAlerts } from '../../../api/expiration';
import {
  HeaderContainer,
  LeftSection,
  RightSection,
  TableWrapper,
  LimitFormControl,
  LimitSelect,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  HeaderCell,
  CheckboxCell,
  StyledTableRow,
  BodyCell,
  CheckboxBodyCell,
  MemberTypeTab,
  MemberTypeLabel,
  MemberTypeCount,
  CheckboxOptionGroup,
  CheckboxOptionItem,
  CheckboxOptionLabel,
  SearchLengthContainer,
  TableLoadingOverlay,
  MessagingButton,
  MessagingMenuPaper,
  MessagingMenuItem,
  GroupManageButton,
  FloatingActionBar,
  FloatingBarGrip,
  FloatingBarInfo,
  FloatingDeleteButton,
  FloatingGroupAddButton,
  FloatingCloseButton,
  ExpirySettingChip,
  ExpirySettingIcon,
  ExpirySettingText,
} from './MemberListStyled';
import { CommonExcelButton } from '../../../form-components/CommonExcelButton';
import DateIcon from '../../../_icons/DateIcon';
import ExpirationExtensionModal from '../modal/expiration/ExpirationExtensionModal';
import StandardModal from '../modal/standard/StandardModal';
import SmsIcon from '../../../_icons/SmsIcon';
import LightVersionOverlay, { useTransientLightVersionOverlay } from '../../common/LightVersionOverlay';
import SmsModal from '../modal/sms/SmsModal';
import MemberPushModal from '../modal/push/MemberPushModal';
import ConfirmModal from '../../common/modal/ConfirmModal';
import MemberGroupModal from '../modal/memberGroup/MemberGroupModal';
import GroupAddPopover from '../modal/memberGroup/GroupAddPopover';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useTableColumns from '../../../hooks/useTableColumns';
import TableColumnSettings from '../../common/tableColumns/TableColumnSettings';
import { MEMBER_LIST_COLUMNS, MEMBER_LIST_TABLE_KEY } from './memberListColumns';

const HintIcon = ({ hint }) => (
  <Tooltip title={hint} arrow placement="top">
    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#9aa0a6', cursor: 'help' }} onClick={e => e.stopPropagation()} />
  </Tooltip>
);

// 커스텀 정렬 헤더
// hint 가 있으면 라벨과 정렬 화살표 사이에 ⓘ 툴팁을 둔다 (라벨 · ⓘ · 화살표 순)
const SortHeader = ({ label, field, sortField, sortDirection, onClick, hint }) => {
  const isActive = sortField === field;
  const isDesc = isActive && sortDirection === 'DESC';
  return (
    <span
      onClick={() => onClick(field)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', cursor: 'pointer', userSelect: 'none' }}>
      {label}
      {hint && <HintIcon hint={hint} />}
      {isActive ? (
        isDesc ? <KeyboardArrowDownIcon sx={{ fontSize: '16px', color: '#0F7F85' }} /> : <KeyboardArrowUpIcon sx={{ fontSize: '16px', color: '#0F7F85' }} />
      ) : (
        <KeyboardArrowDownIcon sx={{ fontSize: '16px', color: '#ccc' }} />
      )}
    </span>
  );
};

const MemberList = ({
  users,
  query,
  setQuery,
  userList,
  appliedQuery,
  memberTypeCounts,
  baseMemberTypes = [],
  activeTab = null,
  onMemberTypeTabClick,
  tableLoading,
}) => {
  const user = useSelector(state => state.user.user);
  const currentBranchId = user?.branch?.branchId;
  const toast = useToast();
  // 지점별 컬럼 설정(표시·순서·왼쪽 고정). 카탈로그는 memberListColumns.jsx
  const { columns, visibleColumns, toggleVisible, moveColumn, setPinned, reset: resetColumns } =
    useTableColumns(MEMBER_LIST_TABLE_KEY, MEMBER_LIST_COLUMNS);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [sortField, setSortField] = useState('lastPaymentDate'); // 정렬 필드
  const [sortDirection, setSortDirection] = useState('DESC'); // 정렬 방향 (대문자)
  const [expirationModalOpen, setExpirationModalOpen] = useState(false); // 만료일 연장 모달
  const [standardModalOpen, setStandardModalOpen] = useState(false); // 만료임박기준 설정 모달
  const [smsForwardingModalOpen, setSmsForwardingModalOpen] = useState(false); // SMS 모달
  const [pushModalOpen, setPushModalOpen] = useState(false); // 회원앱 알림 발송 모달
  // 문자/앱 알림 통합 버튼 — 호버하면 두 채널 중 고르는 팝오버가 뜬다
  const [messagingMenuAnchor, setMessagingMenuAnchor] = useState(null);
  const messagingMenuCloseTimer = useRef(null);

  const openMessagingMenu = e => {
    clearTimeout(messagingMenuCloseTimer.current);
    setMessagingMenuAnchor(e.currentTarget);
  };
  const scheduleCloseMessagingMenu = () => {
    messagingMenuCloseTimer.current = setTimeout(() => setMessagingMenuAnchor(null), 200);
  };
  const closeMessagingMenuNow = () => {
    clearTimeout(messagingMenuCloseTimer.current);
    setMessagingMenuAnchor(null);
  };
  const [memberGroupModalOpen, setMemberGroupModalOpen] = useState(false); // 회원 그룹 관리 모달
  const [groupAddAnchor, setGroupAddAnchor] = useState(null); // 선택 회원 그룹 담기 팝오버 기준 버튼
  // 선택된 회원의 단일 소스. memberId → 회원 객체.
  // 행에 isChecked를 심는 방식이었으나 페이지를 넘길 때마다 users가 통째로 교체돼 선택이 사라졌다.
  // 목록 데이터와 분리해 두어야 페이징·정렬과 무관하게 선택이 유지된다.
  const [selectedMap, setSelectedMap] = useState(() => new Map());
  const [selectAllLoading, setSelectAllLoading] = useState(false); // 전체선택 전량 조회 중
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // 회원 삭제 확인 다이얼로그
  const [deleting, setDeleting] = useState(false); // 삭제 진행 중
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 만료임박 기준 설정 조회 (모달 열려 있을 때만)
  const queryClient = useQueryClient();
  const branchId = user?.branch?.branchId;
  const { data: expirationAlerts } = useQuery({
    queryKey: ['expirationAlerts', branchId],
    queryFn: () => getExpirationAlerts(branchId, user.token),
    enabled: standardModalOpen && !!branchId && !!user?.token,
  });

  // 만료임박 기준 저장 (PATCH)
  const updateAlertsMutation = useMutation({
    mutationFn: payload => updateExpirationAlerts(branchId, payload, user.token),
    onSuccess: () => {
      toast.success(t('만료임박 기준이 저장되었습니다'));
      setStandardModalOpen(false);
      // 설정 캐시 무효화 → 다음 모달 오픈 시 fresh 조회
      queryClient.invalidateQueries({ queryKey: ['expirationAlerts', branchId] });
      // 회원 목록 재조회 (expiringSoon 카운트/대상 갱신을 위해)
      userList(user.token, query);
    },
  });

  // 테이블 드래그 스크롤
  const tableContainerRef = useRef(null);
  const isDraggingTable = useRef(false);
  const hasDraggedTable = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);

  const handleTableMouseDown = e => {
    // 헤더(th)는 컬럼 드래그 정렬에 쓰므로 표 스크롤 드래그에서 제외한다
    if (e.target.closest('button, a, input, .MuiSwitch-root, .MuiCheckbox-root, th')) return;
    isDraggingTable.current = true;
    hasDraggedTable.current = false;
    dragStartX.current = e.pageX;
    dragScrollLeft.current = tableContainerRef.current.scrollLeft;
    tableContainerRef.current.style.cursor = 'grabbing';
    tableContainerRef.current.style.userSelect = 'none';
  };

  const handleTableMouseMove = e => {
    if (!isDraggingTable.current) return;
    const dx = e.pageX - dragStartX.current;
    if (Math.abs(dx) > 3) hasDraggedTable.current = true;
    tableContainerRef.current.scrollLeft = dragScrollLeft.current - dx;
  };

  const handleTableMouseUp = () => {
    isDraggingTable.current = false;
    if (tableContainerRef.current) {
      tableContainerRef.current.style.cursor = 'grab';
      tableContainerRef.current.style.userSelect = '';
    }
  };

  const handleTableClick = e => {
    if (hasDraggedTable.current) {
      e.preventDefault();
      e.stopPropagation();
      hasDraggedTable.current = false;
    }
  };

  // query prop이 변경될 때 정렬 상태 동기화 (새로고침/뒤로가기 복원 시)
  useEffect(() => {
    setSortField(query.sortBy || 'lastPaymentDate');
    setSortDirection(query.sortDirection || 'DESC');
  }, [query.sortBy, query.sortDirection]);

  const changeLimit = event => {
    userList(user.token, { ...query, limit: event.target.value, page: 1 });
    setQuery({ ...query, limit: event.target.value, page: 1 });
  };

  // CustomCheckbox는 root onClick과 input onChange가 모두 걸려 있어 클릭 1회에 핸들러가 2번 불린다.
  // 토글은 멱등해서 문제없지만 전량 조회는 요청이 2번 나가므로 ref로 막는다.
  const selectAllInFlight = useRef(false);
  // 전량 조회 세대 번호. 응답을 기다리는 사이 검색조건이 바뀌면 결과를 버린다.
  const selectAllGeneration = useRef(0);

  // 헤더 체크박스: 현재 검색조건의 전체 회원을 담거나(체크) 선택을 모두 비운다(해제).
  const handleSelectAll = async checked => {
    if (!checked) {
      setSelectedMap(new Map());
      return;
    }
    if (selectAllInFlight.current) return;
    selectAllInFlight.current = true;
    const generation = selectAllGeneration.current;
    setSelectAllLoading(true);
    try {
      const allQuery = { ...query, page: 1, limit: 10000 };
      const result = await getUsersList(user.token, QueryString.stringify(allQuery));
      // 조회 중 검색조건이 바뀌었으면 이전 조건의 회원으로 선택을 덮어쓰지 않는다.
      if (selectAllGeneration.current !== generation) return;
      setSelectedMap(new Map((result?.content || []).map(m => [m.memberId, m])));
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      selectAllInFlight.current = false;
      setSelectAllLoading(false);
    }
  };

  // 행 체크박스: 해당 회원만 토글한다. 다른 페이지의 선택은 건드리지 않는다.
  const toggleMember = (member, checked) => {
    setSelectedMap(prev => {
      const next = new Map(prev);
      if (checked) next.set(member.memberId, member);
      else next.delete(member.memberId);
      return next;
    });
  };

  const exportToExcel = async () => {
    // 선택된 회원이 있으면 페이지와 무관하게 선택 전원을 내려받는다.
    if (selectedMap.size > 0) {
      downloadExcel([...selectedMap.values()]);
    } else {
      // 전체 데이터 API 호출 후 다운로드
      setExcelLoading(true);
      try {
        const excelQuery = { ...query, page: 1, limit: 10000 }; // 전체 데이터 조회
        const queryString = QueryString.stringify(excelQuery);
        const result = await getUsersList(user.token, queryString);
        downloadExcel(result?.content || []);
      } catch (error) {
        toast.error(errorMessages(error));
      } finally {
        setExcelLoading(false);
      }
    }
  };

  const downloadExcel = async dataToExport => {
    try {
      // 화면에 보이는 컬럼·순서 그대로 내린다(컬럼 설정 연동)
      await exportMembersToStyledExcel(dataToExport || [], visibleColumns, columnCtx, '회원목록');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(t('엑셀 다운로드에 실패했습니다'));
    }
  };

  const isGuestUser = user.userRole === 'ROLE_GUEST';
  const liteOverlay = useTransientLightVersionOverlay();
  const canExcel = useUserAuth('MEMBER_EXCEL_DOWNLOAD');
  const canSMS = useUserAuth('MEMBER_SMS');
  const canExpiryAlert = useUserAuth('MEMBER_EXPIRY_ALERT');
  const canExpiryExtend = useUserAuth('MEMBER_EXPIRY_EXTEND');

  // 회원 구분 탭 데이터 (props로 받은 memberTypeCounts 사용 - 필터와 무관하게 고정)
  // ROLE_GUEST일 때: 전체, 유효회원, 만료회원만 표시
  const allMemberTypeTabs = [
    { type: null, label: '전체', count: memberTypeCounts?.total || 0 },
    { type: 'ACTIVE', label: '유효회원', count: memberTypeCounts?.active ?? 0 },
    { type: 'SCHEDULED', label: '사용예정', count: memberTypeCounts?.scheduled || 0 },
    { type: 'EXPIRING_SOON', label: '만료임박', count: memberTypeCounts?.expiringSoon || 0 },
    { type: 'STOPPED', label: '중지회원', count: memberTypeCounts?.stopped || 0 },
    { type: 'UNPAID', label: '미수회원', count: memberTypeCounts?.unpaid || 0 },
    { type: 'LOCKER_EXPIRING', label: '라커만료', count: memberTypeCounts?.lockerExpiring || 0 },
    { type: 'EXPIRED', label: '만료회원', count: memberTypeCounts?.expired || 0 },
  ];

  // GUEST 사용자는 전체, 유효회원, 만료회원만 볼 수 있음
  const guestAllowedTypes = [null, 'ACTIVE', 'EXPIRED'];
  const memberTypeTabs = isGuestUser
    ? allMemberTypeTabs.filter(tab => guestAllowedTypes.includes(tab.type))
    : allMemberTypeTabs;

  // 탭 활성(하이라이트) 여부: activeTab과 일치하면 활성, null이면 "전체"
  const isTabActive = type => (type === null ? activeTab === null : activeTab === type);

  // 탭 클릭 가능 여부: "전체"는 항상, createBox base가 비어있으면 전부, 아니면 base에 포함된 타입만
  const isTabEnabled = type => {
    if (type === null) return true;
    if (baseMemberTypes.length === 0) return true;
    return baseMemberTypes.includes(type);
  };

  // 선택된 뷰 탭에 따른 뱃지 라벨 반환 (단일 뷰 탭이 활성일 때만)
  // activeTab은 '검색 실행/탭 클릭'에서만 바뀌므로, createBox 칩을 선택만 한 상태에선 라벨이 바뀌지 않는다.
  const getBadgeLabel = () => {
    if (!activeTab) return null;
    const selectedTab = memberTypeTabs.find(tab => tab.type === activeTab);
    return selectedTab?.label || null;
  };

  // 정렬 클릭 핸들러
  const handleSortClick = field => {
    const isAsc = sortField === field && sortDirection === 'ASC';
    const newDirection = isAsc ? 'DESC' : 'ASC';
    setSortField(field);
    setSortDirection(newDirection);

    // API 호출 (정렬 파라미터 포함)
    const sortQuery = {
      ...query,
      sortBy: field,
      sortDirection: newDirection,
      page: 1,
    };
    userList(user.token, sortQuery);
    setQuery(sortQuery);
  };

  const selectedMembers = useMemo(() => [...selectedMap.values()], [selectedMap]);

  // 현재 페이지가 통째로 선택돼 있는지 — 헤더 체크박스 표시용
  const isPageAllSelected =
    users?.content?.length > 0 && users.content.every(u => selectedMap.has(u.memberId));

  // 선택 해제 (플로팅 바 닫기)
  const clearSelection = () => setSelectedMap(new Map());

  // 실제로 조회가 실행된 조건이 바뀌면 선택을 비운다.
  // '전체선택 = 그 조건의 전원'이라는 의미가 조건이 바뀐 뒤에도 남아 있으면 안 된다.
  // 컬럼 render/excel/header 에 넘기는 컨텍스트. 기간 필터가 걸린 컬럼은 header(ctx) 가 '검색한 …' 으로 바꿔 표기한다
  const badgeLabel = getBadgeLabel();
  // 설정 팝오버는 i18n 키가 아니라 번역된 라벨을 보여준다
  const translatedColumns = useMemo(() => columns.map(c => ({ ...c, label: t(c.label) })), [columns, t]);
  const columnCtx = useMemo(
    () => ({ t, currentBranchId, badgeLabel, appliedQuery }),
    [t, currentBranchId, badgeLabel, appliedQuery],
  );

  // 왼쪽 고정 컬럼의 sticky left 오프셋. 체크박스 열 + 앞선 고정 컬럼들의 실제 폭을 헤더에서 잰다
  const pinnedColumns = useMemo(() => visibleColumns.filter(c => c.pinned), [visibleColumns]);
  const headerCellRefs = useRef({});
  const [pinnedLefts, setPinnedLefts] = useState({});
  useLayoutEffect(() => {
    // offsetWidth 는 정수로 반올림돼 셀 사이에 1px 틈이 생기므로 실측 폭(소수)을 누적한다
    const width = el => el?.getBoundingClientRect().width || 0;
    let left = width(headerCellRefs.current.__checkbox);
    const next = {};
    pinnedColumns.forEach(column => {
      next[column.key] = left;
      left += width(headerCellRefs.current[column.key]);
    });
    setPinnedLefts(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, [pinnedColumns, users, columnCtx]);
  // 배경색은 MemberListStyled 의 `.sticky-cell` 규칙이 행 상태별로 준다(부트스트랩 .table 규칙보다 특이도 높게)
  const stickySx = key => ({
    position: 'sticky',
    left: pinnedLefts[key] ?? 0,
    zIndex: 2,
  });

  // 헤더 드래그로 컬럼 순서 변경. 다른 헤더 위를 지나는 동안 바로 자리를 바꿔 보여주고(설정 팝오버와 같은 방식),
  // 놓는 자리의 고정 여부를 따라간다. 같은 자리에서 dragover 가 반복돼도 한 번만 옮기도록 서명을 기억한다
  const dragColumnKey = useRef(null);
  const lastHeaderDrop = useRef(null);
  const handleHeaderDragStart = (e, key) => {
    dragColumnKey.current = key;
    lastHeaderDrop.current = null;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  };
  const handleHeaderDragOver = (e, key) => {
    e.preventDefault();
    const from = dragColumnKey.current;
    if (!from || from === key) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const after = e.clientX > rect.left + rect.width / 2;
    const sig = `${key}:${after}`;
    if (lastHeaderDrop.current === sig) return;
    lastHeaderDrop.current = sig;
    moveColumn(from, key, after);
  };
  const handleHeaderDragEnd = () => {
    dragColumnKey.current = null;
    lastHeaderDrop.current = null;
  };

  // query가 아닌 appliedQuery를 쓰는 이유: 상세검색 칩은 누를 때마다 query를 바꾸지만
  // 목록은 '검색' 실행 전까지 그대로다. 보이는 목록은 그대로인데 선택만 사라지면 안 된다.
  // page만 바뀐 경우는 조건 변경이 아니므로 시그니처에서 제외한다.
  const querySignature = useMemo(() => {
    const { page, ...rest } = appliedQuery || {};
    return JSON.stringify(rest);
  }, [appliedQuery]);

  useEffect(() => {
    selectAllGeneration.current += 1;
    setSelectedMap(new Map());
  }, [querySignature]);

  // 플로팅 액션바 드래그 위치. null이면 기본 위치(하단 중앙, styled 정의).
  const [barPos, setBarPos] = useState(null);

  // 선택이 모두 해제되면(바가 사라지면) 드래그 위치를 초기화 → 재선택 시 하단 중앙에서 다시 시작.
  useEffect(() => {
    if (selectedMembers.length === 0) setBarPos(null);
  }, [selectedMembers.length]);
  // 드래그 중 마우스-바 좌상단 offset + 바 크기(뷰포트 밖 이탈 방지용). ref라 리렌더 없이 유지.
  const barDragRef = useRef(null);

  // 이벤트 리스너 add/remove에 동일 참조가 필요해 useCallback으로 고정한다.
  const handleBarMouseMove = useCallback(e => {
    if (!barDragRef.current) return;
    const { offsetX, offsetY, width, height } = barDragRef.current;
    const maxLeft = Math.max(0, window.innerWidth - width);
    const maxTop = Math.max(0, window.innerHeight - height);
    const left = Math.min(Math.max(0, e.clientX - offsetX), maxLeft);
    const top = Math.min(Math.max(0, e.clientY - offsetY), maxTop);
    setBarPos({ left, top });
  }, []);

  const handleBarMouseUp = useCallback(() => {
    barDragRef.current = null;
    document.removeEventListener('mousemove', handleBarMouseMove);
    document.removeEventListener('mouseup', handleBarMouseUp);
  }, [handleBarMouseMove]);

  // 바 배경을 잡고 드래그. 버튼(삭제/닫기) 클릭은 드래그로 취급하지 않는다.
  const handleBarMouseDown = useCallback(
    e => {
      if (e.target.closest('button')) return;
      const rect = e.currentTarget.getBoundingClientRect();
      barDragRef.current = {
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        width: rect.width,
        height: rect.height,
      };
      document.addEventListener('mousemove', handleBarMouseMove);
      document.addEventListener('mouseup', handleBarMouseUp);
    },
    [handleBarMouseMove, handleBarMouseUp],
  );

  // 선택 회원 일괄 소프트 삭제
  const handleDeleteMembers = async () => {
    const memberIds = selectedMembers.map(m => m.memberId).filter(Boolean);
    if (memberIds.length === 0) return;
    setDeleting(true);
    try {
      await deleteMembersBulk(memberIds, user.token);
      toast.success(t('선택한 회원이 삭제되었습니다'));
      setDeleteDialogOpen(false);
      setSelectedMap(new Map());
      // 목록·회원구분 카운트 새로고침
      userList(user.token, query, true);
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <LightVersionOverlay active={liteOverlay.active}>
      <HeaderContainer>
        <LeftSection>
          {memberTypeTabs.map(tab => {
            const isActive = isTabActive(tab.type);
            const enabled = isTabEnabled(tab.type);
            return (
              <MemberTypeTab
                key={tab.type || 'all'}
                isActive={isActive}
                tabType={tab.type}
                disabled={!enabled}
                onClick={() => enabled && onMemberTypeTabClick(tab.type)}>
                <MemberTypeLabel isActive={isActive}>{t(tab.label)}</MemberTypeLabel>
                <MemberTypeCount isActive={isActive}>
                  {tab.count}
                  {t('명')}
                </MemberTypeCount>
              </MemberTypeTab>
            );
          })}
        </LeftSection>
        <RightSection>
          <Tooltip title={!canExcel ? '권한이 없습니다' : ''} arrow>
            <CommonExcelButton
              onClick={canExcel ? exportToExcel : undefined}
              disabled={loading || excelLoading || !users?.content?.length}
              sx={!canExcel ? { opacity: 0.5, cursor: 'default' } : {}}>
              {excelLoading
                ? t('다운로드 중...')
                : selectedMap.size > 0
                  ? t('선택 엑셀 다운로드')
                  : t('전체 엑셀 다운로드')}
            </CommonExcelButton>
          </Tooltip>
          <GroupManageButton
            onClick={isGuestUser ? liteOverlay.show : () => setMemberGroupModalOpen(true)}
            variant="outlined">
            <GroupsOutlinedIcon sx={{ fontSize: 17 }} />
            {t('그룹 관리')}
          </GroupManageButton>
          <Tooltip title={!canSMS && !isGuestUser ? '권한이 없습니다' : ''} arrow>
            {/* 라이트버전은 클릭 시 안내 오버레이 표시. 그 외엔 호버(또는 클릭)로
                문자 / 회원앱 알림 중 고르는 팝오버가 뜬다 */}
            <MessagingButton
              onMouseEnter={!isGuestUser && canSMS ? openMessagingMenu : undefined}
              onMouseLeave={!isGuestUser && canSMS ? scheduleCloseMessagingMenu : undefined}
              onClick={isGuestUser ? liteOverlay.show : canSMS ? openMessagingMenu : undefined}
              variant="outlined"
              sx={!canSMS && !isGuestUser ? { opacity: 0.5, cursor: 'default' } : {}}>
              <SmsIcon />
              {t('문자 / 앱 알림')}
            </MessagingButton>
          </Tooltip>
          <Popper
            open={Boolean(messagingMenuAnchor)}
            anchorEl={messagingMenuAnchor}
            placement="bottom-start"
            style={{ zIndex: 1300 }}>
            <ClickAwayListener onClickAway={closeMessagingMenuNow}>
              <MessagingMenuPaper onMouseEnter={() => clearTimeout(messagingMenuCloseTimer.current)} onMouseLeave={scheduleCloseMessagingMenu}>
                <MessagingMenuItem
                  onClick={() => {
                    closeMessagingMenuNow();
                    setSmsForwardingModalOpen(true);
                  }}>
                  <SmsIcon />
                  {t('문자 보내기')}
                </MessagingMenuItem>
                <MessagingMenuItem
                  onClick={() => {
                    closeMessagingMenuNow();
                    setPushModalOpen(true);
                  }}>
                  <NotificationsActiveOutlinedIcon sx={{ fontSize: 20, color: '#4AB3BC' }} />
                  {t('회원앱 알림 보내기')}
                </MessagingMenuItem>
              </MessagingMenuPaper>
            </ClickAwayListener>
          </Popper>
          <Tooltip title={!canExpiryAlert ? '권한이 없습니다' : ''} arrow>
            <ExpirySettingChip
              onClick={canExpiryAlert ? () => setStandardModalOpen(true) : undefined}
              sx={!canExpiryAlert ? { opacity: 0.5, cursor: 'default' } : {}}>
              <ExpirySettingIcon className="expiry-icon">
                <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
              </ExpirySettingIcon>
              <ExpirySettingText className="expiry-text">{t('만료임박기준')}</ExpirySettingText>
            </ExpirySettingChip>
          </Tooltip>
          {!isGuestUser && (
            <CheckboxOptionGroup>
              <Tooltip title={!canExpiryExtend ? '권한이 없습니다' : ''} arrow>
                <CheckboxOptionItem
                  onClick={canExpiryExtend ? () => setExpirationModalOpen(true) : undefined}
                  sx={!canExpiryExtend ? { opacity: 0.5, cursor: 'default' } : {}}>
                  <DateIcon className="checkbox-icon" />
                  <CheckboxOptionLabel className="checkbox-label">{t('만료일 연장')}</CheckboxOptionLabel>
                </CheckboxOptionItem>
              </Tooltip>
            </CheckboxOptionGroup>
          )}
          <TableColumnSettings
            columns={translatedColumns}
            onToggle={toggleVisible}
            onMove={moveColumn}
            onSetPinned={setPinned}
            onReset={resetColumns}
            hint="여기서 숨긴 컬럼은 엑셀 다운로드에서도 빠지고, 순서도 그대로 적용됩니다. 설정은 이 지점 모든 계정에 공통입니다."
          />
          <LimitFormControl>
            <LimitSelect value={query.limit} name="limit" size="small" onChange={changeLimit}>
              <MenuItem value={25}>{t('25개씩')}</MenuItem>
              <MenuItem value={50}>{t('50개씩')}</MenuItem>
              <MenuItem value={100}>{t('100개씩')}</MenuItem>
            </LimitSelect>
          </LimitFormControl>
        </RightSection>
      </HeaderContainer>
      <SearchLengthContainer>검색 조건 목록 : {users?.totalElements || 0}명</SearchLengthContainer>

      <TableWrapper>
        {tableLoading && (
          <TableLoadingOverlay>
            <CircularProgress size={40} />
          </TableLoadingOverlay>
        )}
        <StyledTableContainer
          id="member-list-table"
          className="table"
          ref={tableContainerRef}
          onMouseDown={handleTableMouseDown}
          onMouseMove={handleTableMouseMove}
          onMouseUp={handleTableMouseUp}
          onMouseLeave={handleTableMouseUp}
          onClickCapture={handleTableClick}
          style={{ cursor: 'grab' }}>
          <StyledTable size="small" aria-label="a dense table">
            <StyledTableHead>
              <TableRow>
                <CheckboxCell align="center" ref={el => (headerCellRefs.current.__checkbox = el)} className="sticky-cell" sx={stickySx('__checkbox')}>
                  <CustomCheckbox
                    color="primary"
                    name="allSelect"
                    checked={isPageAllSelected}
                    disabled={selectAllLoading}
                    onChange={e => handleSelectAll(e.target.checked)}
                  />
                </CheckboxCell>
                {visibleColumns.map(column => {
                  const label = column.header ? column.header(columnCtx) : t(column.label);
                  return (
                    <HeaderCell
                      key={column.key}
                      align="center"
                      ref={el => (headerCellRefs.current[column.key] = el)}
                      draggable
                      onDragStart={e => handleHeaderDragStart(e, column.key)}
                      onDragOver={e => handleHeaderDragOver(e, column.key)}
                      onDrop={e => e.preventDefault()}
                      onDragEnd={handleHeaderDragEnd}
                      className={column.pinned ? 'sticky-cell' : undefined}
                      sx={{ cursor: 'grab', ...(column.pinned ? stickySx(column.key) : {}) }}>
                      {column.sortField ? (
                        <SortHeader label={label} field={column.sortField} sortField={sortField} sortDirection={sortDirection} onClick={handleSortClick} hint={column.hint} />
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                          {label}
                          {column.hint && <HintIcon hint={column.hint} />}
                        </span>
                      )}
                    </HeaderCell>
                  );
                })}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {users?.content?.length > 0 ? (
                users.content.map((memberUser, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <StyledTableRow
                      role="checkbox"
                      key={index}
                      expired={memberUser.memberType === '만료회원'}
                      onClick={() => navigate(`/member/${memberUser.memberId}`)}>
                      <CheckboxBodyCell padding="checkbox" onClick={e => e.stopPropagation()} align="center" className="sticky-cell" sx={stickySx('__checkbox')}>
                        <CustomCheckbox
                          name={memberUser.username + memberUser.memberId}
                          checked={selectedMap.has(memberUser.memberId)}
                          onChange={e => toggleMember(memberUser, e.target.checked)}
                        />
                      </CheckboxBodyCell>
                      {visibleColumns.map(column => (
                        <BodyCell
                          key={column.key}
                          id={labelId}
                          scope="row"
                          padding="none"
                          align={column.align || 'center'}
                          className={column.pinned ? 'sticky-cell' : undefined}
                          sx={{ ...(column.cellSx || {}), ...(column.pinned ? stickySx(column.key) : {}) }}>
                          {column.render(memberUser, columnCtx)}
                        </BodyCell>
                      ))}
                    </StyledTableRow>
                  );
                })
              ) : (
                <TableRow>
                  <BodyCell colSpan={visibleColumns.length + 1} align="center" sx={{ padding: '40px 0', color: '#999' }}>
                    항목이 없습니다
                  </BodyCell>
                </TableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </TableWrapper>

      {/* 만료일 연장 모달 */}
      <ExpirationExtensionModal
        open={expirationModalOpen}
        onClose={() => setExpirationModalOpen(false)}
        memberCount={users?.totalElements || 0}
        memberUsers={users}
        onSubmit={() => {
          // 연장 완료 후 회원 목록 새로고침
          userList(user.token, query);
        }}
      />

      {/* 만료임박기준 설정 모달 */}
      <StandardModal
        open={standardModalOpen}
        onClose={() => setStandardModalOpen(false)}
        initialData={expirationAlerts}
        onSubmit={payload => updateAlertsMutation.mutate(payload)}
      />

      {/* 회원 선택 시 하단 플로팅 액션 바 (그룹 담기·삭제). 바 배경을 잡아 드래그로 위치 이동 가능 */}
      {!isGuestUser && selectedMembers.length > 0 && (
        <FloatingActionBar
          onMouseDown={handleBarMouseDown}
          style={{
            userSelect: 'none',
            cursor: 'move',
            ...(barPos ? { left: barPos.left, top: barPos.top, bottom: 'auto', transform: 'none' } : {}),
          }}>
          <FloatingBarGrip>⠿</FloatingBarGrip>
          <FloatingBarInfo>
            {selectedMembers.length}
            {t('명 선택됨')}
          </FloatingBarInfo>
          <FloatingGroupAddButton
            onClick={e => setGroupAddAnchor(e.currentTarget)}
            variant="outlined">
            <GroupsOutlinedIcon sx={{ fontSize: 17 }} />
            {t('그룹 추가')}
          </FloatingGroupAddButton>
          <FloatingDeleteButton onClick={() => setDeleteDialogOpen(true)} variant="outlined">
            {t('삭제')}
          </FloatingDeleteButton>
          <FloatingCloseButton onClick={clearSelection}>×</FloatingCloseButton>
        </FloatingActionBar>
      )}

      {/* 선택 회원을 여러 그룹에 한 번에 담는 팝오버 */}
      <GroupAddPopover
        open={!!groupAddAnchor}
        anchorEl={groupAddAnchor}
        onClose={() => setGroupAddAnchor(null)}
        selectedMembers={selectedMembers}
      />

      {/* 회원 그룹 관리 모달 — 그룹을 만들고 소속 회원·출결을 본다 */}
      <MemberGroupModal open={memberGroupModalOpen} onClose={() => setMemberGroupModalOpen(false)} />

      {/* SMS 문자 보내기 모달 */}
      <SmsModal
        open={smsForwardingModalOpen}
        onClose={() => setSmsForwardingModalOpen(false)}
        selectedMembers={selectedMembers}
      />

      {/* 회원앱 알림(푸시) 보내기 모달 — 자체 회원 검색/필터/선택 UI를 내장 */}
      <MemberPushModal open={pushModalOpen} onClose={() => setPushModalOpen(false)} />

      {/* 회원 삭제 확인 다이얼로그 (복구 불가 경고) */}
      <ConfirmModal
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        onConfirm={handleDeleteMembers}
        message={`선택한 ${selectedMembers.length}명의 회원을 삭제합니다.\n삭제 후에는 복구할 수 없습니다.`}
        highlightText="복구할 수 없습니다"
        highlightColor="#F44336"
        cancelText="취소"
        confirmText="삭제"
        confirmColor="#F44336"
      />
    </LightVersionOverlay>
  );
};

export default MemberList;
